import {useState, useCallback} from 'react';
import {message} from 'antd';
import {DownloadInfoItem} from "../../../../types/analytics";
import useAuthStore from "../../../../store/authStore";
import useHomeStore from "../../../../store/homeStore";
import {useActiveMarker} from "../../../../store/markerStore";
import useDatasetStore from "../../../../store/datasetStore";
import {usePointStore} from "../../../../store/pointStore";
import useCartStore from "../../../../store/cartStore";
import {EVENT_DATA_ADDED_TO_CART, trackUpdate} from "../../../../utils/matomoEvents";
import {getPointNameCartError} from "../component/Modal/Error/PointNameInCartError";

export function useDatasetCart(
  item: DownloadInfoItem,
  coords: number[],
  label: string,
  namePanel: string,
  price: number,
  isDTU: boolean,
  onAddProductToCart: (
    item: DownloadInfoItem,
    pointName: string,
    category: string,
    coordinates: number[]
  ) => Promise<boolean>,
  onRemoveProductFromCart: (pointName: string, category: string) => void
) {
  const [cartLoading, setCartLoading] = useState<boolean>(false);

  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const setShowLogin = useHomeStore(state => state.setShowLogin);
  const timeStepInterval = useHomeStore(state => state.timeStepInterval);

  const point = useActiveMarker();
  const datasetBriefs = useDatasetStore(state => state.datasetBriefs);
  const fwePoints = usePointStore(state => state.fwePoints);
  const shoppingItems = useCartStore(state => state.shoppingItems);

  const isItemInCart = useCallback((): boolean => {
    if (!point || !point.label) return false;

    const shoppingItemsOfPoint = shoppingItems?.[point.label];
    if (!shoppingItemsOfPoint) return false;

    const category = shoppingItemsOfPoint[label];
    if (!category) return false;

    const sameDataset = category.dataset_id === item.datasetId;
    if (!sameDataset) return false;

    // Also check coordinates to distinguish from points with same name
    return category.location.replace(' ', '') === point.coordinates.join();
  }, [point, shoppingItems, item, label]);

  const addedToCart = isItemInCart();

  const addToCart = async () => {
    if (!point?.label) {
      message.warn('Please give the active point a name');
      return;
    }

    setCartLoading(true);

    const pointIsInCard = isItemInCart();

    try {
      if (pointIsInCard) {
        message.error(getPointNameCartError(point.label));
      } else {
        await onAddProductToCart(
          item,
          point.label,
          label,
          coords
        );

        trackUpdate(EVENT_DATA_ADDED_TO_CART, coords, item.datasetId, "");
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      message.error('Failed to prepare data');
    } finally {
      setCartLoading(false);
    }
  };

  const handleAddToCart = useCallback((): void => {
    if (!isAuthenticated()) {
      setShowLogin(true);
      return;
    }

    addToCart();
  }, [
    isAuthenticated,
    setShowLogin,
    item,
    coords,
    namePanel,
    label,
    price,
    point,
    datasetBriefs,
    fwePoints,
    timeStepInterval,
    isDTU,
    onAddProductToCart,
    isItemInCart
  ]);


  const removeFromCart = async () => {
    if (point === undefined || !point?.label) {
      return;
    }

    setCartLoading(true);
    await onRemoveProductFromCart(point?.label, label);
    setCartLoading(false);
  };

  const handleRemoveFromCart = useCallback((): void => {
    if (!isAuthenticated()) {
      setShowLogin(true);
      return;
    }

    removeFromCart();
  }, [isAuthenticated, setShowLogin, point, label, onRemoveProductFromCart]);

  return {
    cartLoading,
    addedToCart,
    handleAddToCart,
    handleRemoveFromCart
  };
}
